import { useState, useEffect, useRef } from 'react';
import { DiscordSDK } from '@discord/embedded-app-sdk';
import type { CommandResponse } from '@discord/embedded-app-sdk';

interface DiscordUser {
  id: string;
  username: string;
  avatar: string | null;
  discriminator: string;
  global_name: string | null;
}

interface UseDiscordSDKResult {
  isReady: boolean;
  isAuthenticated: boolean;
  user: DiscordUser | null;
  serverId: string | null;
  channelId: string | null;
  error: Error | null;
  sdk: DiscordSDK | null;
  auth: CommandResponse<'authenticate'> | null;
}

// Get client ID from environment
const CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID;

// Create SDK instance at module level (like official examples)
export const discordSdk = new DiscordSDK(CLIENT_ID);

// Module-level state to prevent double initialization in React strict mode
let sdkSetupPromise: Promise<CommandResponse<'authenticate'>> | null = null;
let cachedAuth: CommandResponse<'authenticate'> | null = null;

async function setupDiscordSdk(): Promise<CommandResponse<'authenticate'>> {
  // Return cached result if already setup
  if (cachedAuth) {
    return cachedAuth;
  }

  // Return existing promise if setup is in progress
  if (sdkSetupPromise) {
    return sdkSetupPromise;
  }

  // Start setup
  sdkSetupPromise = (async () => {
    // Wait for SDK to be ready
    await discordSdk.ready();
    console.log('Discord SDK ready');

    // Authorize with Discord Client
    const { code } = await discordSdk.commands.authorize({
      client_id: CLIENT_ID,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: [
        'identify',
        'guilds',
      ],
    });

    // Exchange code for access token via our API
    const response = await fetch('/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', errorText);
      throw new Error(`Token exchange failed: ${errorText}`);
    }

    const { access_token } = await response.json();

    // Authenticate with Discord client
    const authResult = await discordSdk.commands.authenticate({
      access_token,
    });

    if (authResult == null) {
      throw new Error('Authenticate command failed');
    }

    console.log('Discord SDK authenticated:', authResult.user?.username);
    cachedAuth = authResult;
    return authResult;
  })();

  return sdkSetupPromise;
}

export function useDiscordSDK(): UseDiscordSDKResult {
  const [isReady, setIsReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<DiscordUser | null>(null);
  const [serverId, setServerId] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [auth, setAuth] = useState<CommandResponse<'authenticate'> | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Prevent double initialization
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    setupDiscordSdk()
      .then((authResult) => {
        setServerId(discordSdk.guildId || null);
        setChannelId(discordSdk.channelId || null);
        setAuth(authResult);
        if (authResult.user) {
          setUser(authResult.user as DiscordUser);
        }
        setIsAuthenticated(true);
        setIsReady(true);
      })
      .catch((err) => {
        // Properly stringify error for logging
        const errorMessage = err instanceof Error
          ? err.message
          : typeof err === 'object'
            ? JSON.stringify(err)
            : String(err);
        console.error('Discord SDK setup failed:', errorMessage);
        setError(err instanceof Error ? err : new Error(errorMessage));
      });
  }, []);

  return {
    isReady,
    isAuthenticated,
    user,
    serverId,
    channelId,
    error,
    sdk: discordSdk,
    auth,
  };
}
